import { GeometryType } from "@evergis/api";
import React, { FC, useMemo } from "react";
import { LinearProgress } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { SYS_ATTRIBUTES_NAMES } from "../../../../resources/layers";
import { Attributes } from "../../DialogComponents/Attributes/Attributes";
import { SystemAttributeOption } from "../../DialogComponents/Attributes/types";
import { LayerAttribute } from "../../EditResource/components/Layer/LayerAttributeItem";
import { AttributeWithType } from "../../NewLayer/components/AttributeWithType";

import { AttributesContainer, UploadAttributesContainer } from "../styled";
import { UploadAttribute as UploadAttributeType } from "../types";
import { UploadAttribute } from "./UploadAttribute";
import { useDialogInfo } from "../../../../hooks/useDialogs";
import { useLayerAttributes } from "../../../../hooks/layer/usePostgresLayer";
import { useUploadDataContext } from "../uploadDataContext";

export const UploadAttributesList: FC = () => {
  const { t } = useTranslation("dialogs");
  const { resources } = useDialogInfo();
  const {
    uploadAttributes,
    onSelectAllAttributes,
    sysAttributes,
    addSysAttributes,
    deleteSysAttribute,
    updateSysAttribute,
  } = useUploadDataContext();
  const attributes = useLayerAttributes(resources[0], true);
  const attributeNames = Object.keys(attributes);
  const isAllSelected = Object.keys(uploadAttributes).length === attributeNames.length;
  const excludeSysAttributes = useMemo(
    () => attributeNames.filter(name => SYS_ATTRIBUTES_NAMES.includes(name as SystemAttributeOption)),
    [attributeNames],
  );
  const sortedAttributes = useMemo(
    () =>
      attributeNames
        .filter(name => !SYS_ATTRIBUTES_NAMES.includes(name as SystemAttributeOption))
        .sort()
        .map(key => ({ ...attributes[key], id: key })),
    [attributeNames, attributes],
  );

  if (!attributeNames.length) {
    return (
      <AttributesContainer>
        <LinearProgress />
      </AttributesContainer>
    );
  }

  return (
    <UploadAttributesContainer>
      <Attributes
        showTitle={false}
        attributes={sortedAttributes}
        excludeSysAttributes={excludeSysAttributes}
        sysAttributes={sysAttributes}
        addSysAttributes={addSysAttributes}
        allAttributes={isAllSelected}
        toggleAllAttributes={onSelectAllAttributes}
        allAttributesLabel={t("uploadData.importAllAttributes")}
      >
        {(attribute: UploadAttributeType | LayerAttribute) =>
          attribute.sysAttribute ? (
            <AttributeWithType
              {...(attribute as LayerAttribute)}
              onDeleteSysAttribute={deleteSysAttribute}
              onChangeSysAttribute={updateSysAttribute}
              geometryType={GeometryType.Polygon}
            />
          ) : (
            <UploadAttribute {...(attribute as UploadAttributeType)} name={attribute.id} />
          )
        }
      </Attributes>
    </UploadAttributesContainer>
  );
};
